import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import AppraisalPortal from '../components/AppraisalPortal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  TrendingUp, Settings, Users, BarChart3, Calculator, Save, Send, 
  Trophy, Medal, Award, Star, Calendar, ClipboardCheck,
  Plus, Trash2, Target, AlertCircle, Download, Upload
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PerformancePortalPage = ({ user, setUser }) => {
  // State for Manager View
  const [isRM, setIsRM] = useState(false);
  const [kppParameters, setKppParameters] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [currentEvaluation, setCurrentEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // State for Employee View
  const [myScores, setMyScores] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [averageStats, setAverageStats] = useState(null);
  
  // KPP Configuration State
  const [selectedKPPs, setSelectedKPPs] = useState([]);
  const [evaluationMonth, setEvaluationMonth] = useState(new Date().getMonth() + 1);
  const [evaluationYear, setEvaluationYear] = useState(new Date().getFullYear());
  
  // Employee Scores State
  const [employeeScores, setEmployeeScores] = useState([]);
  
  // Dashboard State
  const [dashboardData, setDashboardData] = useState({ evaluations: [], available_years: [] });
  const [dashboardYear, setDashboardYear] = useState(new Date().getFullYear());
  
  // Dialog State
  const [showCalculatedResults, setShowCalculatedResults] = useState(false);
  const [calculatedResults, setCalculatedResults] = useState([]);
  
  const [activeTab, setActiveTab] = useState('employee-view');
  
  // Main view state: 'stack-ranking' or 'appraisal'
  const [mainView, setMainView] = useState('stack-ranking');

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const checkIfRM = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/my-approvals/check-rm`, config);
      setIsRM(response.data.is_rm);
      if (response.data.is_rm) {
        setActiveTab('kpp-setup');
      }
    } catch (error) {
      console.error('Error checking RM status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchKPPParameters = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/kpp/parameters`, config);
      setKppParameters(response.data);
    } catch (error) {
      console.error('Error fetching KPP parameters:', error);
    }
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/performance/team`, config);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, []);

  const fetchEvaluations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/performance/evaluations`, config);
      setEvaluations(response.data);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
    }
  }, []);

  const fetchMyScores = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/performance/my-scores`, config);
      setMyScores(response.data);
    } catch (error) {
      console.error('Error fetching my scores:', error);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/performance/dashboard?year=${dashboardYear}`, config);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, [dashboardYear]);

  const fetchAverageStats = useCallback(async () => {
    if (selectedMonths.length === 0) {
      setAverageStats(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const monthsParam = selectedMonths.join(',');
      const response = await axios.get(`${API}/performance/my-average?months=${monthsParam}`, config);
      setAverageStats(response.data);
    } catch (error) {
      console.error('Error fetching average stats:', error);
    }
  }, [selectedMonths]);

  useEffect(() => {
    checkIfRM();
    fetchKPPParameters();
    fetchMyScores();
  }, [checkIfRM, fetchKPPParameters, fetchMyScores]);

  useEffect(() => {
    if (isRM) {
      fetchTeamMembers();
      fetchEvaluations();
      fetchDashboardData();
    }
  }, [isRM, dashboardYear, fetchTeamMembers, fetchEvaluations, fetchDashboardData]);

  useEffect(() => {
    fetchAverageStats();
  }, [selectedMonths, fetchAverageStats]);

  // Load existing evaluation when month/year changes
  useEffect(() => {
    if (isRM) {
      const existing = evaluations.find(
        e => e.evaluation_month === evaluationMonth && e.evaluation_year === evaluationYear
      );
      if (existing) {
        setCurrentEvaluation(existing);
        setSelectedKPPs(existing.kpp_config.map(kpp => ({
          kpp_id: kpp.kpp_id,
          kpp_name: kpp.kpp_name,
          min_score: kpp.min_score,
          max_score: kpp.max_score,
          max_points: kpp.max_points || 15,
          input_type: kpp.input_type || 'number',
          scoring_criteria: kpp.scoring_criteria || []
        })));
        setEmployeeScores(existing.employee_scores || []);
      } else {
        setCurrentEvaluation(null);
        setSelectedKPPs([]);
        setEmployeeScores([]);
      }
    }
  }, [evaluationMonth, evaluationYear, evaluations, isRM]);

  // Initialize employee scores when team members or KPPs change
  useEffect(() => {
    if (teamMembers.length > 0 && selectedKPPs.length > 0 && (!currentEvaluation || currentEvaluation.employee_scores?.length === 0)) {
      const initialScores = teamMembers.map(member => ({
        employee_id: member.employee_id,
        employee_name: member.employee_name,
        employee_email: member.employee_email,
        employee_code: member.employee_code,
        scores: selectedKPPs.map(kpp => ({
          kpp_id: kpp.kpp_id,
          actual_value: ''
        }))
      }));
      setEmployeeScores(initialScores);
    }
  }, [teamMembers, selectedKPPs]);

  const addKPPToSelection = (kppId) => {
    const kpp = kppParameters.find(k => k.id === kppId);
    if (kpp && !selectedKPPs.find(k => k.kpp_id === kppId)) {
      setSelectedKPPs([...selectedKPPs, {
        kpp_id: kpp.id,
        kpp_name: kpp.name,
        min_score: kpp.min_score,
        max_score: kpp.max_score,
        max_points: kpp.max_points || 15,
        input_type: kpp.input_type || 'number',
        scoring_criteria: kpp.scoring_criteria || []
      }]);
    }
  };

  const removeKPPFromSelection = (kppId) => {
    setSelectedKPPs(selectedKPPs.filter(k => k.kpp_id !== kppId));
  };

  const handleSaveKPPConfig = async () => {
    if (selectedKPPs.length === 0) {
      toast.error('Please select at least one KPP parameter');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        evaluation_month: evaluationMonth,
        evaluation_year: evaluationYear,
        kpp_config: selectedKPPs.map(kpp => ({
          kpp_id: kpp.kpp_id
        }))
      };

      const response = await axios.post(`${API}/performance/evaluation`, payload, config);
      toast.success(response.data.is_new ? 'Evaluation created!' : 'KPP configuration updated!');
      fetchEvaluations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save KPP configuration');
    }
  };

  const updateEmployeeScore = (employeeId, kppId, value) => {
    setEmployeeScores(employeeScores.map(emp => {
      if (emp.employee_id === employeeId) {
        return {
          ...emp,
          scores: emp.scores.map(score => 
            score.kpp_id === kppId ? { ...score, actual_value: value } : score
          )
        };
      }
      return emp;
    }));
  };

  const handleSaveScores = async () => {
    if (!currentEvaluation) {
      toast.error('Please save KPP configuration first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/performance/scores/save`, {
        evaluation_id: currentEvaluation.id,
        employee_scores: employeeScores
      }, config);

      toast.success('Scores saved successfully!');
      fetchEvaluations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scores');
    }
  };

  const handleDownloadTemplate = async () => {
    if (!currentEvaluation) {
      toast.error('Please save KPP configuration first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/performance/scores/download-template/${currentEvaluation.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Score_Template_${getMonthName(evaluationMonth)}_${evaluationYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!currentEvaluation) {
      toast.error('Please save KPP configuration first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/performance/scores/bulk-upload/${currentEvaluation.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(response.data.message);
      if (response.data.skipped?.length > 0) {
        toast.info(`Skipped employees: ${response.data.skipped.join(', ')}`);
      }
      fetchEvaluations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload scores');
    }
    e.target.value = '';
  };

  const handleCalculate = async () => {
    if (!currentEvaluation) {
      toast.error('Please save KPP configuration first');
      return;
    }

    // First save the scores
    await handleSaveScores();

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.post(`${API}/performance/calculate`, {
        evaluation_id: currentEvaluation.id
      }, config);

      setCalculatedResults(response.data.employee_scores);
      setEmployeeScores(response.data.employee_scores);
      setShowCalculatedResults(true);
      toast.success('Scores calculated successfully!');
      fetchEvaluations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate scores');
    }
  };

  const handleSubmit = async () => {
    if (!currentEvaluation || currentEvaluation.status !== 'calculated') {
      toast.error('Please calculate scores before submitting');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.post(`${API}/performance/submit`, {
        evaluation_id: currentEvaluation.id
      }, config);

      toast.success(response.data.message);
      fetchEvaluations();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit evaluation');
    }
  };

  const getRankBadge = (rank, total) => {
    if (rank === 1) return <Badge className="bg-yellow-500"><Trophy className="w-3 h-3 mr-1" />1st</Badge>;
    if (rank === 2) return <Badge className="bg-gray-400"><Medal className="w-3 h-3 mr-1" />2nd</Badge>;
    if (rank === 3) return <Badge className="bg-amber-600"><Award className="w-3 h-3 mr-1" />3rd</Badge>;
    return <Badge variant="outline">#{rank}</Badge>;
  };

  const getMonthName = (month) => months.find(m => m.value === month)?.label || '';

  const toggleMonthSelection = (monthYear) => {
    if (selectedMonths.includes(monthYear)) {
      setSelectedMonths(selectedMonths.filter(m => m !== monthYear));
    } else {
      setSelectedMonths([...selectedMonths, monthYear]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} setUser={setUser} />
        <Navigation user={user} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="performance-portal-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <main className="container mx-auto px-4 py-6 max-w-screen-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            Performance Portal
          </h1>
          <p className="text-gray-600 mt-1">
            {isRM ? 'Evaluate team performance and manage evaluations' : 'View your performance scores and appraisals'}
          </p>
        </div>

        {/* Main View Toggle */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg border bg-white p-1">
            <button
              onClick={() => setMainView('stack-ranking')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                mainView === 'stack-ranking'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="stack-ranking-tab"
            >
              <BarChart3 className="w-4 h-4" />
              Stack Ranking
            </button>
            <button
              onClick={() => setMainView('appraisal')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                mainView === 'appraisal'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="appraisal-tab"
            >
              <ClipboardCheck className="w-4 h-4" />
              Appraisal
            </button>
          </div>
        </div>

        {mainView === 'appraisal' ? (
          <AppraisalPortal user={user} isRM={isRM} />
        ) : isRM ? (
          // Manager View with 3 Tabs
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-3 w-full max-w-lg">
              <TabsTrigger value="kpp-setup" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                KPP Setup
              </TabsTrigger>
              <TabsTrigger value="score-entry" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Score Entry
              </TabsTrigger>
              <TabsTrigger value="dashboard" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: KPP Setup */}
            <TabsContent value="kpp-setup">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Month/Year Selection */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Evaluation Period
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Label>Month</Label>
                        <Select value={String(evaluationMonth)} onValueChange={(v) => setEvaluationMonth(parseInt(v))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {months.map(m => (
                              <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label>Year</Label>
                        <Select value={String(evaluationYear)} onValueChange={(v) => setEvaluationYear(parseInt(v))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026, 2027].map(y => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {currentEvaluation && (
                        <Badge className={
                          currentEvaluation.status === 'submitted' ? 'bg-green-600' :
                          currentEvaluation.status === 'calculated' ? 'bg-blue-600' : 'bg-orange-500'
                        }>
                          {currentEvaluation.status.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Available KPPs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-green-600" />
                      Available KPP Parameters
                    </CardTitle>
                    <CardDescription>Click to add parameters to evaluation (Total Max: {kppParameters.reduce((sum, k) => sum + (k.max_points || 15), 0)} pts)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {kppParameters.length === 0 ? (
                      <p className="text-gray-500 text-sm">No KPP parameters available. Admin needs to configure them.</p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {kppParameters.filter(kpp => !selectedKPPs.find(s => s.kpp_id === kpp.id)).map(kpp => (
                          <div 
                            key={kpp.id}
                            className="p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                            onClick={() => addKPPToSelection(kpp.id)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium">{kpp.name}</p>
                                <p className="text-xs text-gray-500">{kpp.description}</p>
                                {kpp.scoring_criteria?.length > 0 && (
                                  <div className="mt-1 text-xs text-gray-400">
                                    {kpp.scoring_criteria.map((c, i) => (
                                      <span key={i} className="mr-2">
                                        {c.condition === 'equals' && `=${c.value}→${c.points}pts`}
                                        {c.condition === 'greater_than' && `>${c.value}→${c.points}pts`}
                                        {c.condition === 'greater_than_equals' && `≥${c.value}→${c.points}pts`}
                                        {c.condition === 'less_than' && `<${c.value}→${c.points}pts`}
                                        {c.condition === 'less_than_equals' && `≤${c.value}→${c.points}pts`}
                                        {c.condition === 'between' && `${c.value}-${c.value2}→${c.points}pts`}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="text-right ml-2">
                                <Badge className="bg-blue-600">{kpp.max_points || 15} pts</Badge>
                                <p className="text-xs text-gray-400 mt-1">{kpp.min_score}-{kpp.max_score}</p>
                                <Plus className="w-4 h-4 text-blue-600 mt-1 ml-auto" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Selected KPPs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      Selected Parameters
                    </CardTitle>
                    <CardDescription>
                      Total Max Points: <span className="text-green-600 font-bold">{selectedKPPs.reduce((sum, k) => sum + (k.max_points || 15), 0)}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {selectedKPPs.length === 0 ? (
                      <p className="text-gray-500 text-sm">Select parameters from the left panel</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedKPPs.map(kpp => (
                          <div key={kpp.kpp_id} className="p-3 border rounded-lg bg-purple-50">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium">{kpp.kpp_name}</p>
                                <p className="text-xs text-gray-500">Range: {kpp.min_score} - {kpp.max_score}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-blue-600">{kpp.max_points || 15} pts</Badge>
                                <Button variant="ghost" size="sm" onClick={() => removeKPPFromSelection(kpp.kpp_id)} disabled={currentEvaluation?.status === 'submitted'}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <Button 
                          className="w-full mt-4" 
                          onClick={handleSaveKPPConfig}
                          disabled={selectedKPPs.length === 0 || currentEvaluation?.status === 'submitted'}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Save KPP Configuration
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Tab 2: Score Entry */}
            <TabsContent value="score-entry">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Employee Scores - {getMonthName(evaluationMonth)} {evaluationYear}
                    </span>
                    <div className="flex items-center gap-2">
                      {currentEvaluation && currentEvaluation.status !== 'submitted' && (
                        <>
                          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                            <Download className="w-4 h-4 mr-1" /> Download Template
                          </Button>
                          <Button variant="outline" size="sm" asChild data-testid="upload-scores-btn">
                            <label className="cursor-pointer">
                              <Upload className="w-4 h-4 mr-1" /> Bulk Upload
                              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} />
                            </label>
                          </Button>
                        </>
                      )}
                      {currentEvaluation && (
                        <Badge className={
                          currentEvaluation.status === 'submitted' ? 'bg-green-600' :
                          currentEvaluation.status === 'calculated' ? 'bg-blue-600' : 'bg-orange-500'
                        }>
                          {currentEvaluation.status.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!currentEvaluation ? (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 mx-auto text-orange-400 mb-4" />
                      <p className="text-gray-600">Please configure and save KPP parameters first in the &quot;KPP Setup&quot; tab.</p>
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-600">No team members found under your management.</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="p-3 text-left border">Employee</th>
                              {selectedKPPs.map(kpp => (
                                <th key={kpp.kpp_id} className="p-3 text-center border min-w-32">
                                  <div>{kpp.kpp_name}</div>
                                  <div className="text-xs text-gray-500 font-normal">
                                    {kpp.input_type === 'time' ? '(HH:MM:SS)' : `(${kpp.min_score}-${kpp.max_score})`}
                                  </div>
                                  <Badge className="bg-blue-500 text-xs">{kpp.max_points || 15} pts</Badge>
                                </th>
                              ))}
                              {currentEvaluation.status !== 'draft' && (
                                <>
                                  <th className="p-3 text-center border bg-blue-50">
                                    Points
                                    <div className="text-xs font-normal text-gray-500">/{selectedKPPs.reduce((sum, k) => sum + (k.max_points || 15), 0)}</div>
                                  </th>
                                  <th className="p-3 text-center border bg-blue-50">%</th>
                                  <th className="p-3 text-center border bg-yellow-50">Rank</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {employeeScores.map((emp, idx) => (
                              <tr key={emp.employee_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="p-3 border">
                                  <div className="font-medium">{emp.employee_name}</div>
                                  <div className="text-xs text-gray-500">{emp.employee_code}</div>
                                </td>
                                {selectedKPPs.map(kpp => {
                                  const scoreItem = emp.scores?.find(s => s.kpp_id === kpp.kpp_id);
                                  const isTimeInput = kpp.input_type === 'time';
                                  return (
                                    <td key={kpp.kpp_id} className="p-2 border text-center">
                                      {isTimeInput ? (
                                        <Input
                                          type="text"
                                          placeholder="HH:MM:SS"
                                          value={scoreItem?.actual_value || ''}
                                          onChange={(e) => {
                                            // Allow only valid time format characters
                                            const val = e.target.value.replace(/[^0-9:]/g, '');
                                            updateEmployeeScore(emp.employee_id, kpp.kpp_id, val);
                                          }}
                                          className="w-24 mx-auto text-center font-mono"
                                          disabled={currentEvaluation.status === 'submitted'}
                                        />
                                      ) : (
                                        <Input
                                          type="number"
                                          min={kpp.min_score}
                                          max={kpp.max_score}
                                          value={scoreItem?.actual_value || ''}
                                          onChange={(e) => updateEmployeeScore(emp.employee_id, kpp.kpp_id, e.target.value)}
                                          className="w-20 mx-auto text-center"
                                          disabled={currentEvaluation.status === 'submitted'}
                                        />
                                      )}
                                      {currentEvaluation.status !== 'draft' && scoreItem?.earned_points !== undefined && (
                                        <div className="text-xs mt-1 text-green-600 font-medium">{scoreItem.earned_points} pts</div>
                                      )}
                                    </td>
                                  );
                                })}
                                {currentEvaluation.status !== 'draft' && (
                                  <>
                                    <td className="p-3 border text-center font-bold bg-blue-50">
                                      {emp.total_score || '-'}/{emp.max_score || selectedKPPs.reduce((sum, k) => sum + (k.max_points || 15), 0)}
                                    </td>
                                    <td className="p-3 border text-center font-bold bg-blue-50">
                                      {emp.percentage ? `${emp.percentage}%` : '-'}
                                    </td>
                                    <td className="p-3 border text-center bg-yellow-50">
                                      {emp.rank ? getRankBadge(emp.rank, employeeScores.length) : '-'}
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex gap-3 mt-6 justify-end">
                        <Button 
                          variant="outline" 
                          onClick={handleSaveScores}
                          disabled={currentEvaluation.status === 'submitted'}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </Button>
                        <Button 
                          onClick={handleCalculate}
                          disabled={currentEvaluation.status === 'submitted'}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Calculator className="w-4 h-4 mr-2" />
                          Calculate
                        </Button>
                        <Button 
                          onClick={handleSubmit}
                          disabled={currentEvaluation.status !== 'calculated'}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Submit & Email
                        </Button>
                      </div>

                      {currentEvaluation.status === 'submitted' && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-green-800 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            This evaluation has been submitted and emails sent to all employees.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 3: Dashboard */}
            <TabsContent value="dashboard">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                      Historical Performance Data
                    </span>
                    <Select value={String(dashboardYear)} onValueChange={(v) => setDashboardYear(parseInt(v))}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dashboardData.available_years.length > 0 ? 
                          dashboardData.available_years.map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          )) :
                          [2024, 2025, 2026].map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData.evaluations.length === 0 ? (
                    <div className="text-center py-12">
                      <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No submitted evaluations found for {dashboardYear}</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {dashboardData.evaluations.map(evaluation => (
                        <Card key={evaluation.id} className="border-l-4 border-l-purple-500">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center justify-between">
                              <span>{getMonthName(evaluation.evaluation_month)} {evaluation.evaluation_year}</span>
                              <Badge className="bg-green-600">Submitted</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse text-sm">
                                <thead>
                                  <tr className="bg-gray-100">
                                    <th className="p-2 text-left border">Rank</th>
                                    <th className="p-2 text-left border">Employee</th>
                                    {evaluation.kpp_config.map(kpp => (
                                      <th key={kpp.kpp_id} className="p-2 text-center border">{kpp.kpp_name}</th>
                                    ))}
                                    <th className="p-2 text-center border bg-blue-50">Score</th>
                                    <th className="p-2 text-center border bg-blue-50">%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {evaluation.employee_scores?.map((emp, idx) => (
                                    <tr key={emp.employee_id} className={emp.rank <= 3 ? 'bg-yellow-50' : ''}>
                                      <td className="p-2 border text-center">
                                        {getRankBadge(emp.rank, evaluation.employee_scores.length)}
                                      </td>
                                      <td className="p-2 border font-medium">{emp.employee_name}</td>
                                      {evaluation.kpp_config.map(kpp => {
                                        const score = emp.scores?.find(s => s.kpp_id === kpp.kpp_id);
                                        return (
                                          <td key={kpp.kpp_id} className="p-2 border text-center">
                                            {score?.actual_value || '-'}
                                          </td>
                                        );
                                      })}
                                      <td className="p-2 border text-center font-bold bg-blue-50">{emp.total_score}</td>
                                      <td className="p-2 border text-center font-bold bg-blue-50">{emp.percentage}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          // Employee View
          <div className="space-y-6">
            {/* Average Stats Card */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  My Performance Summary
                </CardTitle>
                <CardDescription>Select months to calculate average</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  {myScores.map(score => {
                    const monthYear = `${score.evaluation_month}-${score.evaluation_year}`;
                    const isSelected = selectedMonths.includes(monthYear);
                    return (
                      <Badge 
                        key={score.id}
                        variant={isSelected ? "default" : "outline"}
                        className={`cursor-pointer ${isSelected ? 'bg-blue-600' : ''}`}
                        onClick={() => toggleMonthSelection(monthYear)}
                      >
                        {getMonthName(score.evaluation_month)} {score.evaluation_year}
                      </Badge>
                    );
                  })}
                </div>
                
                {averageStats && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-blue-600">{averageStats.average_percentage}%</p>
                      <p className="text-sm text-gray-500">Average Percentage</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-purple-600">{averageStats.average_score}</p>
                      <p className="text-sm text-gray-500">Average Score</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-green-600">{averageStats.count}</p>
                      <p className="text-sm text-gray-500">Months Selected</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Performance Cards */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Monthly Performance History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myScores.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No performance evaluations found</p>
                    <p className="text-sm text-gray-400">Your manager will submit evaluations that will appear here.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myScores.map(score => (
                      <Card key={score.id} className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center justify-between">
                            <span>{getMonthName(score.evaluation_month)} {score.evaluation_year}</span>
                            {getRankBadge(score.rank, score.total_employees)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Score</span>
                              <span className="font-bold text-lg">{score.total_score}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Percentage</span>
                              <span className="font-bold text-lg text-blue-600">{score.percentage}%</span>
                            </div>
                            <Progress value={score.percentage} className="h-2" />
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">Rank</span>
                              <span className="font-medium">#{score.rank} of {score.total_employees}</span>
                            </div>
                            <div className="border-t pt-3 mt-3">
                              <p className="text-xs text-gray-500 mb-2">KPP Breakdown:</p>
                              <div className="space-y-1">
                                {score.kpp_scores?.map(kpp => (
                                  <div key={kpp.kpp_id} className="flex justify-between text-xs">
                                    <span className="text-gray-600">{kpp.kpp_name || kpp.kpp_id}</span>
                                    <span className="font-medium">{kpp.actual_value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Calculated Results Dialog */}
      <Dialog open={showCalculatedResults} onOpenChange={setShowCalculatedResults}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Stack Ranking Results - {getMonthName(evaluationMonth)} {evaluationYear}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th className="p-3 text-left">Rank</th>
                  <th className="p-3 text-left">Employee</th>
                  <th className="p-3 text-center">Total Score</th>
                  <th className="p-3 text-center">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {calculatedResults.map((emp, idx) => (
                  <tr 
                    key={emp.employee_id} 
                    className={emp.rank <= 3 ? 'bg-yellow-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="p-3 border">
                      {getRankBadge(emp.rank, calculatedResults.length)}
                    </td>
                    <td className="p-3 border font-medium">{emp.employee_name}</td>
                    <td className="p-3 border text-center font-bold">{emp.total_score}</td>
                    <td className="p-3 border text-center">
                      <span className="font-bold text-blue-600">{emp.percentage}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCalculatedResults(false)}>
              Close
            </Button>
            <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700">
              <Send className="w-4 h-4 mr-2" />
              Submit & Send Emails
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Missing import fix
const CheckCircle = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

export default PerformancePortalPage;
